import React, { useState, useRef, useEffect } from 'react';
import "./Dropdown.css"
import { ReactComponent as ExpandIcon } from "../../../assets/icons/arrow-down.svg";
import { DropdownItemType } from '../../../types/common.type';

// Define the props interface for the Dropdown component
interface DropdownProps {
    options: DropdownItemType[]; // An array of strings for the dropdown items
    initialValue?: number; // Optional initial selected value
    onSelect?: (selectedOption: number) => void; // Optional callback for selection
}

// Dropdown component
const Dropdown = ({ options, initialValue, onSelect }: DropdownProps) => {
    // State to manage whether the dropdown list is open or closed
    const [isOpen, setIsOpen] = useState(false);
    // State to manage the currently selected value
    // Ensure initialValue is one of the options, otherwise default to the first option
    const [selectedIndex, setSelectedIndex] = useState(initialValue ? initialValue : 0);

    // Ref to detect clicks outside the dropdown to close it
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Effect to handle clicks outside the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // If the click is outside the dropdown component, close it
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        // Add event listener for clicks
        document.addEventListener('mousedown', handleClickOutside);
        // Cleanup the event listener on component unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    // Function to toggle the dropdown's open/closed state
    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    // Function to handle selection of an option
    const handleSelect = (index: number) => {
        setSelectedIndex(index); // Set the new selected value
        setIsOpen(false); // Close the dropdown
        if (onSelect) {
            onSelect(index); // Call the onSelect callback if provided
        }
    };

    return (
        <div className="bell-widget-dd" ref={dropdownRef}>
            <button
                type="button"
                className="bell-widget-dd-btn"
                id="bell-widget-dd-btn"
                aria-expanded={isOpen}
                aria-haspopup="true"
                onClick={handleToggle}
            >
                <div className='bell-widget-dd-btn-item-container'>
                    {options[selectedIndex].icon && <div className='bell-widget-dd-btn-item-icon'>{options[selectedIndex].icon}</div>}
                    <span className='bell-widget-dd-btn-item-label'>{options[selectedIndex].label}</span>
                </div>
                <ExpandIcon className="bell-widget-dd-btn-expand-icon" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>
            {
                isOpen && (
                    <div
                        className="bell-widget-dd-menu"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="menu-button"
                        data-open={isOpen} // Custom attribute for styling transitions
                    >
                        <div className="bell-widget-dd-opts" role="none">
                            {options.map((option: any, index: number) => (
                                <button
                                    key={index}
                                    className="bell-widget-dd-item-button"
                                    role="menuitem"
                                    onClick={() => handleSelect(index)}
                                >
                                    <div className='bell-widget-dd-opts-item-container'>
                                        {option.icon && <div className='bell-widget-dd-opts-item-icon'>{option.icon}</div>}
                                        <span className='bell-widget-dd-opts-item-label'>{option.label}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default Dropdown;